import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import api from '../../services/api';
import styles from './SettingsPage.module.css';

const LoadFailureNotice = ({ message, onRetry }) => (
  <>
    <p className={styles.cardDesc} role="alert">{message}</p>
    <div className={styles.formActions}>
      <Button variant="secondary" onClick={onRetry}>
        Try again
      </Button>
    </div>
  </>
);

const SettingsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAchPromo, setSavingAchPromo] = useState(false);
  const [settingsError, setSettingsError] = useState(false);
  const [achPromoError, setAchPromoError] = useState(false);

  const [settings, setSettings] = useState({
    company: {
      name: '',
      email: '',
      phone: '',
      address: ''
    },
    notifications: {
      emailEnabled: true,
      telegramEnabled: false,
      telegramChatId: ''
    },
    integrations: {
      drCareEnabled: false,
      pharmacyEnabled: false
    }
  });

  const [achPromo, setAchPromo] = useState({
    enabled: true,
    discountType: 'free_month',
    discountValue: 1
  });

  useEffect(() => {
    loadSettings();
    loadAchPromoConfig();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      if (response.settings) {
        setSettings(response.settings);
      }
      setSettingsError(false);
    } catch (error) {
      // A failed load leaves `settings` at this component's hardcoded defaults. handleSave PUTs the
      // WHOLE object and settingsRepository.updateMany upserts every key it is given, so saving
      // those defaults overwrites the stored config. Flag it, so the form says so and Save is held.
      setSettingsError(true);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const loadAchPromoConfig = async () => {
    try {
      const response = await api.get('/admin/settings/ach-promo');
      if (response.achPromo) {
        setAchPromo(response.achPromo);
      }
      setAchPromoError(false);
    } catch (error) {
      // Was "silently fail - will use defaults": on screen those defaults are indistinguishable
      // from stored values, and saving them overwrites the real promo configuration.
      setAchPromoError(true);
      console.error('Failed to load ACH promo config:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAchPromo = async () => {
    setSavingAchPromo(true);
    try {
      await api.put('/admin/settings/ach-promo', achPromo);
      toast.success('ACH promotion settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save ACH promotion settings');
    } finally {
      setSavingAchPromo(false);
    }
  };

  const updateCompany = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      company: { ...prev.company, [field]: value }
    }));
  };

  const updateNotifications = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      notifications: { ...prev.notifications, [field]: value }
    }));
  };

  const updateAchPromo = (field, value) => {
    setAchPromo((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Settings</h1>
        <p>Configure your platform settings</p>
      </div>

      {settingsError && (
        <Card className={styles.card}>
          <LoadFailureNotice
            message="Could not load platform settings. The Company Information and Notifications fields below are blank defaults, not your saved values, so saving would overwrite them."
            onRetry={() => loadSettings()}
          />
        </Card>
      )}

      {/* ACH Promotion */}
      <Card className={styles.card}>
        <h2>ACH Promotion</h2>
        <p className={styles.cardDesc}>
          Offer a discount to first-time customers who pay via ACH bank transfer.
        </p>

        {achPromoError && (
          <LoadFailureNotice
            message="Could not load the ACH promotion configuration. The values below are defaults, not your saved settings, so saving would overwrite them."
            onRetry={() => loadAchPromoConfig()}
          />
        )}
        <div className={styles.toggleList}>
          <div className={styles.toggleItem}>
            <div>
              <span className={styles.toggleLabel}>Enable ACH Promotion</span>
              <span className={styles.toggleDesc}>Show promo modal to eligible customers at checkout</span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={achPromo.enabled}
                onChange={(e) => updateAchPromo('enabled', e.target.checked)}
              />
              <span className={styles.slider}></span>
            </label>
          </div>
        </div>

        {achPromo.enabled && (
          <div className={styles.achPromoForm}>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Discount Type</label>
              <select
                className={styles.select}
                value={achPromo.discountType}
                onChange={(e) => updateAchPromo('discountType', e.target.value)}
              >
                <option value="free_month">Free Month</option>
                <option value="percentage">Percentage Off</option>
                <option value="fixed">Fixed Amount Off</option>
              </select>
            </div>

            {achPromo.discountType !== 'free_month' && (
              <div className={styles.formRow}>
                <Input
                  label={achPromo.discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount ($)'}
                  type="number"
                  min="0"
                  step={achPromo.discountType === 'percentage' ? '1' : '0.01'}
                  value={achPromo.discountValue}
                  onChange={(e) => updateAchPromo('discountValue', parseFloat(e.target.value) || 0)}
                  placeholder={achPromo.discountType === 'percentage' ? 'e.g., 10' : 'e.g., 25.00'}
                />
              </div>
            )}

            <div className={styles.formActions}>
              <Button onClick={handleSaveAchPromo} loading={savingAchPromo} disabled={achPromoError}>
                Save ACH Promotion
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Company Information */}
      <Card className={styles.card}>
        <h2>Company Information</h2>
        <div className={styles.form}>
          <Input
            label="Company Name"
            value={settings.company.name}
            onChange={(e) => updateCompany('name', e.target.value)}
          />
          <Input
            label="Contact Email"
            type="email"
            value={settings.company.email}
            onChange={(e) => updateCompany('email', e.target.value)}
          />
          <Input
            label="Phone Number"
            value={settings.company.phone}
            onChange={(e) => updateCompany('phone', e.target.value)}
          />
          <Input
            label="Address"
            value={settings.company.address}
            onChange={(e) => updateCompany('address', e.target.value)}
          />
        </div>
      </Card>

      {/* Notifications */}
      <Card className={styles.card}>
        <h2>Notifications</h2>
        <div className={styles.toggleList}>
          <div className={styles.toggleItem}>
            <div>
              <span className={styles.toggleLabel}>Email Notifications</span>
              <span className={styles.toggleDesc}>Send email notifications to customers. Account security email (verification, password reset) is always sent.</span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={settings.notifications.emailEnabled}
                onChange={(e) => updateNotifications('emailEnabled', e.target.checked)}
              />
              <span className={styles.slider}></span>
            </label>
          </div>

          <div className={styles.toggleItem}>
            <div>
              <span className={styles.toggleLabel}>Telegram Notifications</span>
              <span className={styles.toggleDesc}>Send logs and errors to Telegram</span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={settings.notifications.telegramEnabled}
                onChange={(e) => updateNotifications('telegramEnabled', e.target.checked)}
              />
              <span className={styles.slider}></span>
            </label>
          </div>

          {settings.notifications.telegramEnabled && (
            <Input
              label="Telegram Chat ID"
              value={settings.notifications.telegramChatId}
              onChange={(e) => updateNotifications('telegramChatId', e.target.value)}
              placeholder="Enter your Telegram chat ID"
            />
          )}
        </div>
      </Card>

      {/* API Access */}
      <Card className={styles.card}>
        <h2>API Access</h2>
        <p className={styles.cardDesc}>
          Partner integrations submit members programmatically with an agency API
          key. Keys are issued per agency on the agency record and validated
          against the agencies table, so there is no self-service generation here.
        </p>

        <div className={styles.apiDocs}>
          <h3>API Endpoint</h3>
          <code>POST /api/partner/members</code>
          <p>Include the agency API key in the X-API-Key header:</p>
          <code>X-API-Key: YOUR_API_KEY</code>
        </div>
      </Card>

      {/* Save Button */}
      <div className={styles.saveBar}>
        <Button onClick={handleSave} loading={saving} size="large" disabled={settingsError}>
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
